// src/pages/admin/TrashedSubBranchList.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import DataTable from "../list/DataTable";

import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import RestoreIcon from "@mui/icons-material/Restore";

import { API_URL } from "config";
import FlagLoader from "layouts/flagloader";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const TrashedSubBranchList = () => {
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasSubBranchPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasSubBranchPermission("sub_branch.pages.trashed.view");
  const canCreate =
    hasSubBranchPermission("sub_branch.pages.create.view") &&
    hasSubBranchPermission("sub_branch.actions.open_create");
  const canOpenList =
    hasSubBranchPermission("sub_branch.pages.list.view") &&
    hasSubBranchPermission("sub_branch.actions.open_list");
  const canRestore = hasSubBranchPermission("sub_branch.actions.restore");
  const canConfirmRestore = hasSubBranchPermission("sub_branch.actions.confirm_restore");
  const canCancelRestore = hasSubBranchPermission("sub_branch.actions.cancel_restore");
  const canViewField = useCallback(
    (field) => hasSubBranchPermission(`sub_branch.fields.${field}.view`),
    [hasSubBranchPermission]
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/sub-branches/trashed/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch trashed sub branches");
      }

      const json = await res.json();
      setData(json.data?.data || json.data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error loading trashed data",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [canViewPage, fetchData, isAuthReady]);

  const handleRestoreClick = (event, item) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!canRestore) {
      setSnackbar({
        open: true,
        message: "You do not have permission to restore sub branches.",
        color: "error",
      });
      return;
    }
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!canRestore || !canConfirmRestore) {
      setSnackbar({
        open: true,
        message: "You do not have permission to confirm restore.",
        color: "error",
      });
      return;
    }

    if (!selectedItem) return;

    try {
      setRestoringId(selectedItem.id);

      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/sub-branches/restore/${selectedItem.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Restore failed");
      }

      setSnackbar({
        open: true,
        message: "Sub Branch restored successfully",
        color: "success",
      });

      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Restore failed",
        color: "error",
      });
    } finally {
      setRestoringId(null);
      setDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const closeRestoreDialog = () => {
    if (!canCancelRestore) return;
    setDialogOpen(false);
    setSelectedItem(null);
  };
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const columns = [];
  if (canViewField("id")) columns.push({ Header: "ID", accessor: "id" });
  if (canViewField("branch")) columns.push({ Header: "Branch", accessor: "branch" });
  if (canViewField("name")) columns.push({ Header: "Sub Branch", accessor: "name" });
  if (canViewField("deleted_at")) columns.push({ Header: "Deleted At", accessor: "deleted_at" });
  if (canViewField("status")) columns.push({ Header: "Status", accessor: "status" });
  if (canViewField("actions") && canRestore) {
    columns.push({ Header: "Actions", accessor: "actions", disableSortBy: true });
  }

  const rows = data.map((item) => ({
    id: item.id,
    branch: item.branch?.name || "-",
    name: item.name,
    deleted_at: (
      <SoftTypography variant="body2">{new Date(item.deleted_at).toLocaleString()}</SoftTypography>
    ),
    status: (
      <SoftTypography variant="caption" color={item.deleted_at || !item.status ? "error" : "info"}>
        {item.deleted_at || !item.status ? "INACTIVE" : "ACTIVE"}
      </SoftTypography>
    ),
    actions: (
      <>
        {canRestore && (
          <Tooltip title="Restore Sub Branch">
            <IconButton
              size="small"
              color="success"
              onClick={(event) => handleRestoreClick(event, item)}
              disabled={restoringId === item.id}
            >
              {restoringId === item.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RestoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="70vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Trashed Sub Branch List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view trashed sub branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Sub Branch Management"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Trashed Sub Branch List
          </SoftTypography>

          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/sub-branches/add")}
              >
                Add New Sub Branch
              </SoftButton>
            )}
            {canOpenList && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>list</Icon>}
                onClick={() => navigate("/sub-branches")}
              >
                Sub Branch List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        {columns.length > 0 ? (
          <DataTable table={{ columns, rows }} entriesPerPage={false} showTotalEntries={false} />
        ) : (
          <SoftBox p={3}>
            <SoftTypography variant="body2" color="text">
              No visible fields are assigned for this page.
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>

      <Dialog open={dialogOpen} onClose={closeRestoreDialog}>
        <DialogTitle>Confirm Restore</DialogTitle>

        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to restore the sub branch <strong>{selectedItem?.name}</strong>?
          </SoftTypography>
        </DialogContent>

        <DialogActions>
          {canConfirmRestore && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleRestoreConfirm}
              disabled={restoringId === selectedItem?.id}
              endIcon={restoringId === selectedItem?.id ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {restoringId === selectedItem?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}

          {canCancelRestore && (
            <SoftButton color="error" variant="contained" onClick={closeRestoreDialog}>
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default TrashedSubBranchList;
